/**
 * A compact ES | EN switch.
 *
 * It exists because the portal's other two language controls — the TopBar
 * toggle and the Perfil screen — both live inside the authenticated shell. That
 * left English unreachable to anyone who had not signed in: the sign-in screen
 * is `es-ES` by default and offered no way out of it, so half of a contracted
 * bilingual product was invisible to a reviewer looking at `/login`.
 *
 * Languages are labelled by their endonyms (Español, English) rather than by
 * flags or translated names: a flag denotes a country and not a language, and
 * "Spanish"/"Español" flipping with the UI means the label a user is looking for
 * is the one that just disappeared.
 */

import { useTranslation } from 'react-i18next'

import i18n, { rememberLanguage, SUPPORTED_LANGUAGES, type Language } from '../app/i18n.ts'

const ENDONYM: Record<Language, string> = {
  es: 'Español',
  en: 'English',
}

export function LanguagePicker({ className }: { className?: string }) {
  const { t, i18n: instance } = useTranslation()
  const current: Language = instance.language.startsWith('en') ? 'en' : 'es'

  return (
    <div
      className={['lang-switch', className ?? ''].filter(Boolean).join(' ')}
      role="group"
      aria-label={t('common.language')}
    >
      {SUPPORTED_LANGUAGES.map((language) => (
        <button
          key={language}
          // Explicitly not a submit button: this renders inside the sign-in
          // form, where the default would post the form instead.
          type="button"
          lang={language}
          aria-pressed={language === current}
          aria-label={ENDONYM[language]}
          title={ENDONYM[language]}
          onClick={() => {
            rememberLanguage(language)
            void i18n.changeLanguage(language)
          }}
        >
          {language.toUpperCase()}
        </button>
      ))}
    </div>
  )
}
